import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { localRunnerCommand } from "@/lib/connect-token";
import { useConnectToken } from "@/hooks/use-connect-token";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { TerminalBlock } from "@/components/ui/terminal-block";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Circle, Cpu, Globe, Info, Terminal } from "lucide-react";

const DEMO_FALLBACK_TOKEN = // gitleaks:allow
  "eyJhIjoiaHR0cHM6Ly9pYW11Lms2bi5uZXQiLCJnIjoiaHR0cHM6Ly9ncnBjLmlhbXUuazZuLm5ldCJ9";
const isDemoMode = import.meta.env.VITE_MSW === "true";

interface ConnectedRunner {
  id: string;
  name: string;
  platform: string;
  arch: string;
  status: string;
}

function platformLabel(platform: string, arch: string): string {
  const os =
    platform === "darwin" ? "macOS" : platform === "linux" ? "Linux" : platform;
  return `${os} ${arch}`;
}

const verifyLines = (osLabel: string, chromePath: string) => [
  { text: "iamu-runner installer", type: "output" as const },
  { text: osLabel, type: "comment" as const },
  { text: "", type: "output" as const },
  { text: "Downloading iamu-runner...", type: "comment" as const },
  { text: "✓ Downloaded iamu-runner", type: "output" as const },
  { text: "", type: "output" as const },
  {
    text: "✓ Starting runner — press Ctrl+C to stop",
    type: "output" as const,
  },
  { text: "", type: "output" as const },
  { text: `✓ Chrome found at ${chromePath}`, type: "output" as const },
  { text: "● Connected — waiting for jobs", type: "output" as const },
];

export function LocalRunnerIntegration() {
  const { data: projects } = trpc.project.list.useQuery();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );

  const projectId = selectedProjectId ?? projects?.[0]?.id ?? null;
  const {
    token: connectToken,
    error: tokenError,
    loading: tokenLoading,
    generate: generateToken,
  } = useConnectToken(projectId, isDemoMode ? DEMO_FALLBACK_TOKEN : null);

  const runners: ConnectedRunner[] = [];
  const hasConnectedRunners =
    runners?.some((r) => r.status === "idle" || r.status === "running") ??
    false;

  const renderInstallSection = (
    heading: string,
    terminalLabel: string,
    cmd: string,
    osLabel: string,
    chromePath: string,
  ) => (
    <div className="space-y-6">
      <section className="space-y-3">
        <h3 className="text-sm font-semibold tracking-tight">{heading}</h3>
        <p className="text-sm text-muted-foreground">
          Downloads the runner, connects to this project, and cleans up when you
          stop it. Nothing is installed permanently.
        </p>
        {tokenLoading ? (
          <Skeleton className="h-20 rounded-xl" />
        ) : tokenError ? (
          <div className="rounded-xl bg-mark-important/10 p-4 text-sm text-muted-foreground">
            Could not generate a connect token. Check your connection and try
            again.
          </div>
        ) : connectToken ? (
          <TerminalBlock label={terminalLabel} lines={[cmd]} />
        ) : (
          <Button
            onClick={generateToken}
            disabled={!projectId}
            variant="outline"
          >
            Generate connect token
          </Button>
        )}
      </section>
      {connectToken && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold tracking-tight">Verify</h3>
          <p className="text-sm text-muted-foreground">
            You should see the runner connect:
          </p>
          <TerminalBlock
            label="terminal"
            lines={verifyLines(osLabel, chromePath)}
            copyText=""
          />
        </section>
      )}
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-md">
              <Terminal className="h-4 w-4" />
              Local runner
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Test private and localhost URLs with an AI-driven browser agent
              running on your machine.
            </p>
          </div>
          {hasConnectedRunners && (
            <Badge variant="default" className="rounded-sm">
              Connected
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-8">
        {projects && projects.length > 1 && (
          <div className="space-y-2">
            <label htmlFor="runner-project" className="text-sm font-medium">
              Project
            </label>
            <Select
              value={projectId ?? ""}
              onValueChange={setSelectedProjectId}
            >
              <SelectTrigger id="runner-project" className="w-64">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p: { id: string; name: string }) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Tabs defaultValue="mac-linux">
          <TabsList variant="line">
            <TabsTrigger value="mac-linux">macOS / Linux</TabsTrigger>
            <TabsTrigger value="windows">Windows</TabsTrigger>
          </TabsList>
          <TabsContent value="mac-linux" className="pt-6">
            {renderInstallSection(
              "Run this in your terminal",
              "terminal",
              connectToken ? localRunnerCommand(connectToken) : "",
              "macOS arm64",
              "/Applications/Google Chrome.app/...",
            )}
          </TabsContent>
          <TabsContent value="windows" className="pt-6">
            {renderInstallSection(
              "Run this in PowerShell",
              "PowerShell",
              `$env:IAMU_CONNECT='${connectToken ?? ""}'; irm https://iamu.dev/r.ps1 | iex`,
              "Windows amd64",
              "C:\\Program Files\\Google\\Chrome\\...",
            )}
          </TabsContent>
        </Tabs>

        <div className="space-y-3">
          <div className="flex gap-3 rounded-xl bg-primary/5 p-4">
            <Globe className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            <div className="text-sm text-foreground">
              <p className="font-medium">Requires Chrome or Chromium</p>
              <p className="mt-1 text-muted-foreground">
                The runner uses your system Chrome to automate browser testing.
                In non-headless mode (the default), you'll see the browser
                window open and can interact with it — log in, dismiss popups —
                before the AI takes over.
              </p>
            </div>
          </div>
          <div className="flex gap-3 rounded-xl bg-mark-important/10 p-4">
            <Info className="h-4 w-4 mt-0.5 shrink-0 text-severity-important" />
            <div className="text-sm text-foreground">
              <p className="font-medium">Firewall and network</p>
              <p className="mt-1 text-muted-foreground">
                The runner needs outbound access to{" "}
                <code className="text-xs font-mono bg-muted px-1 rounded">
                  iamu.dev
                </code>{" "}
                (HTTPS + gRPC). If you're behind a corporate firewall or VPN,
                you may need to allow these connections.
              </p>
            </div>
          </div>
        </div>

        <section className="space-y-4">
          <h3 className="text-base font-semibold tracking-tight">
            What happens next
          </h3>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 text-sm">
            <dt className="font-medium text-foreground">Run a walkthrough</dt>
            <dd className="text-muted-foreground">
              Go to any scenario, set a localhost target URL, and start a
              walkthrough. The runner picks it up automatically.
            </dd>
            <dt className="font-medium text-foreground">Watch it happen</dt>
            <dd className="text-muted-foreground">
              Chrome opens visibly on your machine. If the site needs a login,
              the runner pauses and lets you authenticate first.
            </dd>
            <dt className="font-medium text-foreground">Stop anytime</dt>
            <dd className="text-muted-foreground">
              Press{" "}
              <kbd className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">
                Ctrl+C
              </kbd>{" "}
              in the terminal. The runner deregisters cleanly.
            </dd>
          </dl>
        </section>

        {runners && runners.length > 0 ? (
          <section className="space-y-3">
            <h3 className="text-sm font-semibold tracking-tight">
              Connected runners
            </h3>
            <div className="space-y-2">
              {runners.map((runner) => (
                <div
                  key={runner.id}
                  className="flex items-center justify-between gap-3 rounded-xl bg-muted/30 p-4"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Cpu className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{runner.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {platformLabel(runner.platform, runner.arch)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Circle
                      className={`h-2 w-2 fill-current ${
                        runner.status === "idle" || runner.status === "running"
                          ? "text-finding-helps"
                          : "text-muted-foreground"
                      }`}
                    />
                    <span className="text-xs text-muted-foreground capitalize">
                      {runner.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : (
          <div className="flex items-center gap-3 rounded-xl bg-muted/30 p-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
              <Info className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              No runners connected yet. Run the install command above to get
              started.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
